import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Truck, Wallet, ImagePlus, Globe2 } from "lucide-react";

export default function Sell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const cta = () => {
    if (!user) { navigate("/register"); return; }
    navigate(user.role === "artist" || user.role === "admin" ? "/artist" : "/register");
  };

  return (
    <div>
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 hero-fade-up">
            <div className="overline-badge">For Artists</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight mt-5">
              Sell your art to a global audience.
            </h1>
            <p className="text-base text-[var(--text-secondary)] leading-relaxed mt-6 max-w-lg">
              ARTFUTUR lists your work in front of collectors, designers and businesses worldwide.
              We handle the sale and the buyer's payment — and you're paid the moment your artwork is delivered.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={cta} data-testid="sell-start-btn"
                className="btn-gold px-7 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2">
                Start selling <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/gallery" className="px-7 py-3.5 rounded-full text-sm font-semibold border border-[var(--text-primary)] hover:bg-[var(--text-primary)] hover:text-white transition-colors">
                See the gallery
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 hero-fade-up">
            <img src="https://images.unsplash.com/photo-1633887091273-a3bd71efddde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Artist in studio" className="rounded-2xl w-full aspect-[4/3] object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          [ImagePlus, "List in minutes", "Upload your artwork with photos, dimensions and story. It goes live to a global audience instantly."],
          [Globe2, "Reach real buyers", "Retail collectors, hotels, gyms, venues and interior designers browse and buy directly."],
          [Truck, "Ship with tracking", "When your work sells, ship it with tracking. The sooner it arrives, the sooner you're paid."],
          [Wallet, "Prompt payout", "The buyer pays at purchase; we hold the funds and send your payout as soon as delivery is confirmed."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-7 border border-[var(--border-subtle)]">
            <div className="w-11 h-11 rounded-full bg-[var(--gold-light)] flex items-center justify-center text-[var(--gold-hover)]"><Icon className="w-5 h-5" /></div>
            <h3 className="font-serif text-xl mt-4">{t}</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      <section className="bg-[var(--bg-dark)] text-white relative grain">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="overline-badge">Simple, fair pricing</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4">You set the price. We take a flat 20% when it sells.</h2>
          <p className="text-stone-400 mt-4 max-w-2xl mx-auto">
            No listing fees, no subscriptions. ARTFUTUR keeps a flat 20% commission only on completed sales —
            covering global reach, secure payment handling and buyer support. You keep 80% of every sale.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-10 text-left">
            {[["Example sale", "€5,000"], ["Platform commission (20%)", "€1,000"], ["Your payout (80%)", "€4,000"]].map(([l, v], i) => (
              <div key={l} className={`glass-dark rounded-xl p-6 ${i === 2 ? "border-[var(--gold)]/40" : ""}`}>
                <div className="text-xs text-stone-400">{l}</div>
                <div className={`font-serif text-3xl mt-1 ${i === 2 ? "text-[var(--gold)]" : ""}`}>{v}</div>
              </div>
            ))}
          </div>
          <button onClick={cta} className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold mt-10">
            Become an ARTFUTUR artist <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
